import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { LoadingIndicator } from '@dojo/widgets/loading-indicator';
import { MicroCredentialCard } from '@holmescorp/card';
import { childmap, createNlsMiddleware, keymap, lccstate, pathmap, userstate } from 'lcc-state';
import { Icon } from '@holmescorp/base-widgets';
import css from '../../css/progress-view.m.css';
import { ContentContainer, MicroProgress, MicroCardButton, svc, ScoreChart, ScoreToast } from '../index';
const factory = create({
    icache: createICacheMiddleware(),
    nls: createNlsMiddleware('ditabook'),
    theme,
    lccstate,
    userstate
});
export const AllTestsPanel = factory(({ middleware: { icache, theme, lccstate, userstate, nls } }) => {
    const { messages: { card_button_label_start = '[ditabook] card_button_label_start', card_button_label_start_quiz = '[ditabook] card_button_label_start_quiz', card_button_label_middle = '[ditabook] card_button_label_middle', card_button_label_finished = '[ditabook] card_button_label_finished', use_progress_chart_color = 'false', assess_progress_not_started = '[ditabook] assess_progress_not_started', assess_progress_threshold_low_label = '[ditabook] assess_progress_threshold_low_label', assess_progress_threshold_mid_label = '[ditabook] assess_progress_threshold_mid_label', assess_progress_threshold_high_label = '[ditabook] assess_progress_threshold_high_label', assess_progress_threshold_high = '80' } } = nls();
    const alertMessages = {
        none: assess_progress_not_started,
        low: assess_progress_threshold_low_label,
        mid: assess_progress_threshold_mid_label,
        high: assess_progress_threshold_high_label
    };
    const { get, path } = lccstate;
    const { get: uget, path: upath } = userstate;
    const { portal_site: site, portal_userid: userid, product: product_id, course_session } = window.hcstatic;
    const session_id = course_session && course_session.pk;
    const themeCss = theme.classes(css);
    const useProgressChartColor = use_progress_chart_color === 'true';
    const passingScore = parseInt(assess_progress_threshold_high) || 80;
    const allBestScores = icache.getOrSet('best_scores', async () => {
        const member_id = userid || '';
        const data = await svc.module_best({ member_id, product_id, session_id, site });
        return data;
    });
    const status = uget(upath('status'));
    const roots = get(path('ditaroots'));
    const topicsets = roots.flatMap((root) => {
        const kidkeys = childmap.get(root) || [];
        return kidkeys.map((tgf) => {
            const { path } = keymap.get(tgf);
            const { topic: { topicset } } = pathmap.get(path);
            const progress = status[tgf];
            return { topicset, progress };
        });
    });
    const getQuizAction = (key, isquizpassed, isreadingcompleted) => {
        return v('div', {
            xqa: `quiz-action-button-${key}`,
            classes: [themeCss.actionButton]
        }, [
            w(MicroCardButton, {
                labelText: card_button_label_start_quiz,
                showIcon: false,
                darkMode: isreadingcompleted && !isquizpassed,
                disabled: isquizpassed,
                onClick: () => {
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(`${path}/quiz`);
                    view();
                },
                onDown: () => {
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(`${path}/quiz`);
                    view();
                }
            })
        ]);
    };
    const getReadingProgress = (isreadingstarted, topicgroups, topicsetkey) => {
        const totalTopicGroups = topicgroups.length;
        const totalTopicGroupsCompleted = topicgroups.filter((tg) => tg.progress.complete === tg.progress.total).length;
        return isreadingstarted
            ? v('div', { classes: [themeCss.innerContainer] }, [
                v('p', {
                    classes: [themeCss.readingProgress],
                    xqa: `reading-progress-${topicsetkey}`
                }, [`Total Reading Progress: ${totalTopicGroupsCompleted} of ${totalTopicGroups}`]),
                v('div', { xqa: `reading-progress-bars-${topicsetkey}` }, topicgroups.map((tg) => {
                    return w(MicroProgress, {
                        label: tg.attributes?.title,
                        min: 0,
                        max: tg.progress.total,
                        value: tg.progress.complete,
                        showOutput: false
                    });
                }))
            ])
            : null;
    };
    const getReadingAction = (key, isreadingstarted, isreadingcompleted) => {
        return v('div', {
            key: `${key}-topicgroup`,
            classes: [themeCss.actionButton]
        }, [
            w(MicroCardButton, {
                labelText: isreadingstarted
                    ? isreadingcompleted
                        ? card_button_label_finished
                        : card_button_label_middle
                    : card_button_label_start,
                showIcon: true,
                darkMode: !isreadingcompleted,
                icon: isreadingcompleted ? w(Icon, { faname: 'rotate-left' }) : null,
                xqa: `topicgroup-reading-action-${key}`,
                onClick: () => {
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(path);
                    view();
                },
                onDown: () => {
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(path);
                    view();
                }
            })
        ]);
    };
    const getTopicPanel = (tgf, allBestScores) => {
        const { topicset, progress } = tgf;
        const { data } = progress;
        const topicgroups = topicset.children
            ? topicset.children
                ?.filter((tg) => !('shortdesc' in tg))
                .map((tg) => ({
                attributes: tg.topicgroup.attributes,
                progress: status[tg.topicgroup.attributes?.key || ''].data
            }))
            : [];
        const isreadingstarted = data.complete > 0;
        const isreadingcompleted = data.complete === data.total;
        const isquizcompleted = data.pointsincorrect > 0 || data.pointscorrect > 0;
        const topicsetkey = topicset.attributes?.key || '';
        const rawScore = data.percent < 1 ? data.percent * 100 : data.percent;
        const isquizpassed = rawScore >= passingScore;
        const score = Math.round(isquizcompleted ? rawScore : -1);
        const bestScoreRecord = allBestScores?.find((qs) => qs.quiz_id === topicset.attributes?.key && qs.score.completed);
        const bestScore = bestScoreRecord ? parseInt(bestScoreRecord.score.score) : -1;
        return w(ContentContainer, { additionalClasses: [themeCss.panel] }, [
            w(ScoreToast, { score, messages: alertMessages, useMidScore: false }),
            w(MicroCredentialCard, { xqa: `module-tests-card-${topicsetkey}` }, [
                {
                    leading: v('div', {}, [
                        v('img', {
                            title: topicset.attributes?.title,
                            src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/icons/icon_${topicsetkey}.png`
                        })
                    ]),
                    trailing: v('div', { classes: [themeCss.title] }, [topicset.attributes?.title]),
                    actions: v('div', {
                        classes: [themeCss.actionContainer]
                    }, [
                        v('div', { classes: [themeCss.innerContainer] }, [
                            w(ScoreChart, {
                                score,
                                bestScore,
                                insideLabelText: `Quiz Score`,
                                useScoreColor: useProgressChartColor,
                                useBestScoreIcon: false
                            })
                        ]),
                        getReadingProgress(isreadingstarted, topicgroups, topicsetkey),
                        v('div', { classes: [themeCss.actionButtons] }, [
                            getReadingAction(topicsetkey, isreadingstarted, isreadingcompleted),
                            getQuizAction(topicsetkey, isquizpassed, isreadingcompleted)
                        ])
                    ])
                }
            ])
        ]);
    };
    const groupedTopicsets = topicsets.reduce((acc, tgf, index) => {
        const itemsPerColumn = Math.ceil(topicsets.length / 3);
        const rawColumnIndex = Math.floor(index / itemsPerColumn);
        const columnIndex = Math.min(2, rawColumnIndex);
        if (!acc[columnIndex]) {
            acc[columnIndex] = [];
        }
        acc[columnIndex].push(tgf);
        return acc;
    }, []);
    return v('div', { classes: [themeCss.progressContainer] }, allBestScores
        ? [
            v('div', { classes: [themeCss.panels] }, groupedTopicsets.map((group) => v('div', { classes: [themeCss.panelColumn] }, group.map((tgf) => getTopicPanel(tgf, allBestScores)))))
        ]
        : [w(LoadingIndicator, { active: true, type: 'circular-medium' })]);
});
export default AllTestsPanel;
